import React, { useImperativeHandle, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@core/store';
import { WcnButton, WcnTextField } from '@/components';

export interface DeleteShopConfirmationDialogProps {
    heading: string;
    message: string;
    expectedShopName: string;
    onConfirm: (confirmationText: string) => void;
    onClose?: () => void;
}

export interface DeleteShopConfirmationDialogRef {
    openDialog: () => void;
    close: () => void;
}

type Props = DeleteShopConfirmationDialogProps & {
    dialogRef?: React.Ref<DeleteShopConfirmationDialogRef>;
};

const DeleteShopConfirmationDialog = ({ heading, message, expectedShopName, onConfirm, onClose, dialogRef }: Props) => {
    const { t } = useTranslation();
    const { darkMode } = useSettingsStore();
    const [open, setOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => {
            setConfirmationText('');
            setOpen(true);
        },
        close: () => {
            setOpen(false);
            setConfirmationText('');
        }
    }));

    const handleClose = () => {
        onClose?.();
        setOpen(false);
        setConfirmationText('');
    };

    const handleConfirm = () => {
        if (confirmationText === expectedShopName) {
            onConfirm(confirmationText);
            setOpen(false);
            setConfirmationText('');
        }
    };

    const isConfirmDisabled = confirmationText !== expectedShopName;
    const hasError = confirmationText !== '' && confirmationText !== expectedShopName;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: darkMode ? 'var(--dark-bg-lighter)' : 'white',
                    color: darkMode ? 'white' : 'var(--gray-dark)'
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    wordBreak: 'break-word'
                }}
            >
                {heading}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Typography
                        sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line',
                            mb: 3
                        }}
                    >
                        {message}
                    </Typography>

                    <WcnTextField
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        label={t('typeToConfirmName', { name: expectedShopName })}
                        placeholder={expectedShopName}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={hasError}
                        helperText={hasError ? t('mustTypeNameToConfirm', { name: expectedShopName }) : ''}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: darkMode ? 'white' : 'var(--gray-dark)',
                                '& fieldset': {
                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                                },
                                '&:hover fieldset': {
                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                            }
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <WcnButton onClick={handleClose} variant="primary">
                    {t('no')}
                </WcnButton>

                <WcnButton onClick={handleConfirm} disabled={isConfirmDisabled} variant="red" color="error">
                    {t('yes')}
                </WcnButton>
            </DialogActions>
        </Dialog>
    );
};

DeleteShopConfirmationDialog.displayName = 'DeleteShopConfirmationDialog';

export default DeleteShopConfirmationDialog;
